import type { IconName } from "@/components/ui/Icon";
import { PERM } from "@/lib/auth/rbac";

/**
 * Modullar ro'yxati — dev_spec.md §11.1 jadvalining o'zi.
 *
 * Har bir modul o'zi talab qiladigan vakolatni ATAYLAB e'lon
 * qiladi: vakolati yo'q modul navigatsiyada ko'rinmaydi. Bu
 * xavfsizlik chorasi EMAS (server baribir tekshiradi) — bu
 * foydalanuvchiga bajarib bo'lmaydigan amalni ko'rsatmaslik
 * uchun (dev_spec.md §12, AT-029).
 *
 * Modullar BO'LIMLARGA biriktirilgan. O'n ikkita element bitta
 * uzun ro'yxatda turganda ular orasidagi farq yo'qoladi: xodim
 * kerakligini ko'z bilan qidiradi. Bo'lim esa savolga javob
 * beradi — "bu narsa nimaga tegishli": omborning O'ZIGA,
 * undagi ISHGA, TAHLILGA yoki TIZIMGA.
 */
export type NavGroup = "warehouse" | "operations" | "analytics" | "system";

/** Bo'lim sarlavhalari — yon panelda shu tartibda chiqadi. */
export const NAV_GROUPS: Array<{ id: NavGroup; label: string }> = [
  { id: "warehouse", label: "Ombor" },
  { id: "operations", label: "Operatsiya" },
  { id: "analytics", label: "Tahlil" },
  { id: "system", label: "Tizim" },
];

export interface NavItem {
  path: string;
  label: string;
  title: string;
  hint: string;
  icon: IconName;
  group: NavGroup;
  /** Shu vakolatlardan KAMIDA BITTASI bo'lsa modul ko'rinadi. */
  permissions: string[];
}

export const NAV_ITEMS: NavItem[] = [
  {
    path: "/",
    label: "Boshqaruv",
    title: "Boshqaruv paneli",
    hint: "Navbat, faol topshiriqlar, kutayotgan qarorlar va kamomadlar.",
    icon: "dashboard",
    group: "warehouse",
    permissions: [PERM.taskRead, PERM.reportRead],
  },
  {
    path: "/topology",
    label: "Topologiya",
    title: "Topologiya",
    hint: "Ombor kataklari: joy, vazn va SKU chegarasi. Katakni tanlasangiz ichidagi tovarlar ko'rinadi.",
    icon: "topology",
    group: "warehouse",
    permissions: [PERM.topologyRead],
  },
  {
    path: "/map",
    label: "Xarita",
    title: "Ombor xaritasi",
    hint: "Qator, stellaj, qavat va yacheyka chizmasi; bandlik va bloklar.",
    icon: "map",
    group: "warehouse",
    permissions: [PERM.reportRead],
  },
  {
    path: "/map-3d",
    label: "3D xarita",
    title: "3D xarita",
    hint: "Butun ombor 3D da: xodim va kara qayerda, palletlar kimniki, marshrut yo'laklar va o'tish stellajlari bo'yicha.",
    icon: "layers",
    group: "warehouse",
    permissions: [PERM.reportRead],
  },
  {
    path: "/skus",
    label: "Tovar",
    title: "Tovarlar",
    hint: "Tovar kartalari: VGH, ИКПУ, shtrix-kod, mo'rtlik; yorliq PDF.",
    icon: "scan",
    group: "warehouse",
    permissions: [PERM.masterDataRead],
  },
  {
    path: "/stock",
    label: "Qoldiq",
    title: "Qoldiq",
    hint: "SKU, LP va yacheyka kesimida qoldiq; band va mavjud miqdor.",
    icon: "stock",
    group: "warehouse",
    permissions: [PERM.inventoryRead, PERM.reportRead],
  },
  {
    path: "/pallets",
    label: "Pallet",
    title: "Palletlar va tara",
    hint: "LP ro'yxati: holati, qayerdaligi va tarkibi. Yorliq shu yerdan chiqariladi va qayta chop etiladi.",
    icon: "layers",
    group: "warehouse",
    permissions: [PERM.inventoryRead],
  },
  {
    path: "/journal",
    label: "Jurnal",
    title: "Ombor jurnali",
    hint: "Yacheyka va pallet amallari: kim, qachon, qaysi LP, qayerdan qayerga, nima va qancha; katak, LP, xodim, amal va sana bo'yicha.",
    icon: "audit",
    group: "warehouse",
    permissions: [PERM.inventoryRead],
  },
  {
    path: "/tasks",
    label: "Topshiriq",
    title: "Topshiriqlar",
    hint: "Navbat: tur bo'yicha, ustuvorlik tartibida. Qatorni sudrab tartibni o'zgartiring.",
    icon: "task",
    group: "operations",
    // Hisobot emas, task-service navbati — `report.read` endi shart emas.
    permissions: [PERM.taskRead],
  },
  {
    path: "/task-history",
    label: "Topshiriq tarixi",
    title: "Topshiriqlar tarixi",
    hint: "Yakunlangan va bekor qilingan topshiriqlar: kim bajardi, qachon, reja va fakt, holat tarixi.",
    icon: "audit",
    group: "operations",
    // Navbat bilan bir manba (task-service) va bir vakolat.
    permissions: [PERM.taskRead],
  },
  {
    path: "/orders",
    label: "Buyurtmalar",
    title: "Buyurtmalar",
    hint: "Chiqim buyurtmalari. «Terishga chiqarish» bitta oynada: xodimni tanlash, pallet rejasi (3D va PDF), tara va teruvchilarga topshiriq.",
    icon: "layers",
    group: "operations",
    permissions: [PERM.pickingRead],
  },
  {
    path: "/receiving-sessions",
    label: "Qabul sessiyalari",
    title: "Qabul sessiyalari",
    hint: "TSD da topshiriqsiz prixod: Excel hisobot, 1C nakladnoyi raqamini biriktirish va solishtirish — keyin palletlar karachilarga.",
    icon: "pallet",
    group: "operations",
    permissions: [PERM.inboundRead],
  },
  {
    path: "/return-sessions",
    label: "Vozvrat sessiyalari",
    title: "Vozvrat sessiyalari",
    hint: "TSD da topshiriqsiz vozvrat: Excel, 1C vozvrat nakladnoyi va solishtirish — keyin vozvrat xodimining o'ziga OX ga joylash.",
    icon: "refresh",
    group: "operations",
    permissions: [PERM.inboundRead],
  },
  {
    path: "/decisions",
    label: "Qaror",
    title: "Menejer qarorlari",
    hint: "Nazorat kamomadi va qabul farqi: qaror hujjatni o'zi yakunlaydi.",
    icon: "check",
    group: "operations",
    permissions: [PERM.managerDecision, PERM.inboundApprove],
  },
  {
    path: "/blocks",
    label: "Blok",
    title: "Bloklangan manzillar",
    hint: "Sabab, qamrov va manba; ochish-yopish izi.",
    icon: "block",
    group: "operations",
    permissions: [PERM.inventoryRead],
  },
  {
    path: "/discrepancies",
    label: "Tafovut",
    title: "Tafovutlar",
    hint: "Reja va fakt farqi, manba tarixi, inventarizatsiya yakuni.",
    icon: "discrepancy",
    group: "operations",
    permissions: [PERM.reportRead, PERM.controlRead],
  },
  {
    path: "/counts",
    label: "Inventar",
    title: "Inventarizatsiya",
    hint: "Sanash topshiriqlari, qayta sanash va tasdiqlash (INV-001..005).",
    icon: "count",
    group: "operations",
    permissions: [PERM.countRead],
  },
  {
    path: "/reports",
    label: "Hisobot",
    title: "Hisobotlar",
    hint: "Barcha xodimlar: davr bo'yicha topshiriq, satr, dona, ish vaqti, satr/soat va tafovutlar. Qatorni bosing — xodim tarixi; CSV eksport.",
    icon: "report",
    group: "analytics",
    permissions: [PERM.reportRead],
  },
  {
    path: "/abc-xyz",
    label: "ABC/XYZ",
    title: "ABC / XYZ tasnifi",
    hint: "Terish soni (ABC) va haftalik talab barqarorligi (XYZ); matritsa va eksport.",
    icon: "layers",
    group: "analytics",
    permissions: [PERM.reportRead],
  },
  {
    path: "/quality",
    label: "Sifat",
    title: "Sifat va foydalanish",
    hint: "RPT-03: karta sifati, bojxona, OX siz SKU, ABC zona, noto'g'ri LP, harakatsiz qoldiq.",
    icon: "filter",
    group: "analytics",
    permissions: [PERM.reportRead],
  },
  {
    path: "/notifications",
    label: "Xabar",
    title: "Xabarnomalar",
    hint: "NTF-001 marshrutlari, yetkazish holati va dead-letter.",
    icon: "bell",
    group: "analytics",
    permissions: [PERM.notificationRead],
  },
  {
    path: "/integration",
    label: "Integratsiya",
    title: "Integratsiya",
    hint: "1C almashinuvi: xabarlar, rad etish sababi, qayta ishlash va 1C holati.",
    icon: "integration",
    group: "system",
    permissions: [PERM.integrationRead],
  },
  {
    path: "/reconciliation",
    label: "Solishtirish",
    title: "1C solishtirish",
    hint: "§7.8: WMS va 1C ni solishtirish — 1C tasdiqlamagan yetkazmalar va hujjat kesimlari. Hech narsani tuzatmaydi.",
    icon: "integration",
    group: "system",
    permissions: [PERM.integrationReconcile],
  },
  {
    path: "/imports",
    label: "Import",
    title: "Import",
    hint: "MIG-01: tovarlar, kataklar reyestri va boshlang'ich qoldiq fayldan; avval tekshiruv.",
    icon: "layers",
    group: "system",
    permissions: [PERM.masterDataImport, PERM.topologyImport, PERM.inventoryAdjust, PERM.inboundApprove, PERM.inboundArrive],
  },
  {
    path: "/audit",
    label: "Audit",
    title: "Audit jurnali",
    hint: "Kim, qachon, nimani qildi; oldingi va keyingi holat.",
    icon: "audit",
    group: "system",
    permissions: [PERM.auditRead],
  },
  {
    path: "/settings",
    label: "Sozlama",
    title: "Biznes sozlamalari",
    hint: "OX min/max, slotting, SLA, xabar va feature flaglar.",
    icon: "settings",
    group: "system",
    permissions: [PERM.configRead],
  },
  {
    path: "/backup",
    label: "Zaxira",
    title: "Serverdan tashqari zaxira",
    hint: "Telegram boti, guruh va kalit oluvchi; oxirgi yuborish natijasi.",
    icon: "backup",
    group: "system",
    permissions: [PERM.offsiteRead],
  },
  {
    path: "/security",
    label: "Xavfsizlik",
    title: "Foydalanuvchi va rollar",
    hint: "Xodimlar, rollar, vakolatlar va qurilmalar.",
    icon: "shield",
    group: "system",
    permissions: [PERM.userRead, PERM.roleManage],
  },
];

export function visibleNavItems(permissions: string[]): NavItem[] {
  return NAV_ITEMS.filter((item) =>
    item.permissions.some((permission) => permissions.includes(permission)),
  );
}

/**
 * Ko'rinadigan modullarni bo'limlarga ajratadi.
 *
 * BO'SH bo'lim qaytarilmaydi: vakolati yo'q xodimda butun bo'lim
 * ochilmasligi mumkin va sarlavhaning o'zi osilib qolardi.
 */
export function groupedNavItems(
  permissions: string[],
): Array<{ id: NavGroup; label: string; items: NavItem[] }> {
  const visible = visibleNavItems(permissions);
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: visible.filter((item) => item.group === group.id),
  })).filter((group) => group.items.length > 0);
}

export function navItemByPath(path: string): NavItem | undefined {
  // Eng uzun mos keluvchi yo'l: `/tasks/123` ham `/tasks` moduliga
  // tegishli.
  return [...NAV_ITEMS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => (item.path === "/" ? path === "/" : path.startsWith(item.path)));
}
